import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { supabase } from '../lib/supabase';
import { ArrowLeft } from 'lucide-react';
import type { Challenge } from '../types';

export function ChallengeEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChallenge();
  }, [id]);

  const fetchChallenge = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('challenges')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      
      setChallenge({
        ...data,
        startDate: new Date(data.start_date),
        endDate: new Date(data.end_date),
        participants: data.participants || 0,
        isActive: data.is_active
      });
    } catch (err) {
      console.error('Error fetching challenge:', err);
      navigate('/challenges');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!challenge) return;

    try {
      const { error } = await supabase
        .from('challenges')
        .update({
          title: challenge.title,
          description: challenge.description,
          start_date: challenge.startDate.toISOString(),
          end_date: challenge.endDate.toISOString(),
          reward: challenge.reward,
          is_active: challenge.isActive
        })
        .eq('id', id);

      if (error) throw error;
      navigate('/challenges');
    } catch (err) {
      console.error('Error updating challenge:', err);
      alert('Failed to update challenge');
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <button
          onClick={() => navigate('/challenges')}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Challenges</span>
        </button>

        <h1 className="text-3xl font-bold">Edit Challenge</h1>

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : challenge ? (
          <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Title</label>
              <input
                type="text"
                value={challenge.title}
                onChange={(e) => setChallenge({ ...challenge, title: e.target.value })}
                className="w-full px-4 py-2 bg-[#2A2B2E] rounded-xl"
                required
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Description</label>
              <textarea
                value={challenge.description}
                onChange={(e) => setChallenge({ ...challenge, description: e.target.value })}
                className="w-full px-4 py-2 bg-[#2A2B2E] rounded-xl h-32 resize-none"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Start Date</label>
                <input
                  type="datetime-local"
                  value={challenge.startDate.toISOString().slice(0, 16)}
                  onChange={(e) => setChallenge({ ...challenge, startDate: new Date(e.target.value) })}
                  className="w-full px-4 py-2 bg-[#2A2B2E] rounded-xl"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">End Date</label>
                <input
                  type="datetime-local"
                  value={challenge.endDate.toISOString().slice(0, 16)}
                  onChange={(e) => setChallenge({ ...challenge, endDate: new Date(e.target.value) })}
                  className="w-full px-4 py-2 bg-[#2A2B2E] rounded-xl"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Reward</label>
              <input
                type="text"
                value={challenge.reward}
                onChange={(e) => setChallenge({ ...challenge, reward: e.target.value })}
                className="w-full px-4 py-2 bg-[#2A2B2E] rounded-xl"
                required
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={challenge.isActive}
                onChange={(e) => setChallenge({ ...challenge, isActive: e.target.checked })}
                className="rounded bg-[#2A2B2E]"
                id="isActive"
              />
              <label htmlFor="isActive" className="text-sm">Active Challenge</label>
            </div>

            <button
              type="submit"
              className="w-full bg-[var(--accent-color)] text-black rounded-xl py-3 font-medium hover:opacity-90 transition-opacity"
            >
              Update Challenge
            </button>
          </form>
        ) : (
          <div className="text-center py-8 text-gray-400">
            Challenge not found
          </div>
        )}
      </div>
    </Layout>
  );
}